import type { Node } from "@xyflow/react";
import { v4 as uuidv4 } from "uuid";
import { WidgetPreview } from "../components/toolbar/tray/trays/widgetPlates";
import type { WidgetPreset } from "../nodes/widgets";
import { EMOJI_FACE, type EmojiEntry } from "../nodes/widgets/ds";
import { readLabelStyle } from "./useCanvasLabelStyle";
import { buildWidgetNode } from "./useCanvasWidgetQuickPlace";

/**
 * What a tray plate arms (ROUND2 §3, ROUND3 §3): one contract for a widget
 * preset and for an emoji, which places a text label holding the character.
 * The ghost is the item at its own size; `build` is the node a pane click
 * adds at the ghost's position.
 */
export interface PlacementItem {
  id: string;
  label: string;
  width: number;
  height: number;
  /** The bar tile that lights while this is armed. */
  tool: "stickers" | "blocks";
  ghost: React.ReactNode;
  build: (position: { x: number; y: number }) => Node;
}

export function widgetPlacementItem(preset: WidgetPreset): PlacementItem {
  return {
    id: preset.id,
    label: preset.label,
    width: preset.defaultWidth,
    height: preset.defaultHeight,
    tool: preset.family ? "stickers" : "blocks",
    ghost: (
      <WidgetPreview
        preset={preset}
        width={preset.defaultWidth}
        height={preset.defaultHeight}
        inset={0}
      />
    ),
    build: (position) => buildWidgetNode(preset, position),
  };
}

/** The top of the label size ladder: an emoji is placed large. */
export const EMOJI_LABEL_SIZE = "48px";
const EMOJI_BOX = 64;

const EMOJI_ID_PREFIX = "emoji-";

/** The bare emoji key behind an armed item's id, or null for anything else. */
export function armedEmojiKey(value: string | null | undefined): string | null {
  return value && value.startsWith(EMOJI_ID_PREFIX)
    ? value.slice(EMOJI_ID_PREFIX.length)
    : null;
}

/** A Unicode character in a text run: a `canvasText` label holding it. */
export function emojiPlacementItem(entry: EmojiEntry): PlacementItem {
  return {
    id: `${EMOJI_ID_PREFIX}${entry.key}`,
    label: entry.name,
    width: EMOJI_BOX,
    height: EMOJI_BOX,
    tool: "stickers",
    ghost: (
      <span
        className="flex h-full w-full items-center justify-center leading-none"
        style={{ fontFamily: EMOJI_FACE, fontSize: EMOJI_LABEL_SIZE }}
        aria-hidden="true"
      >
        {entry.char}
      </span>
    ),
    build: (position) => {
      const style = readLabelStyle();
      return {
        id: uuidv4(),
        type: "canvasText",
        position,
        data: {
          content: entry.char,
          font_family: style.font_family,
          font_size: EMOJI_LABEL_SIZE,
          text_color: style.text_color,
          is_bold: false,
          is_italic: false,
          is_underline: false,
        },
      };
    },
  };
}
